/**
 * FlurrySkillEffect — Blades tree slash-path evolution, replaces Slash.
 *
 * Three rapid sequential sub-strikes, each at a different arc subsector.
 * Each sub-hit resolves independently through the damage pipeline
 * (separate crit rolls, separate Whetstone checks). Total arc coverage
 * equals Slash.
 *
 * Phase machine: idle → active (3 timed sub-hits) → cooldown → idle.
 * Cooldown starts after the final sub-hit completes.
 *
 * Queries resolver with 'slash' for upgrade inheritance (Extended Arc
 * arcDegrees, damage multiplier). Medium cooldown tier.
 *
 * Implements SkillEffect directly with own cooldown and phase management.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Flurry
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FlurryDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startArcAttack(attackType: string, arcDegrees: number): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FlurryConfig {
  readonly cooldownMs: number;
  readonly subHitCount: number;
  readonly subHitIntervalMs: number;
  readonly baseArcDegrees: number;
  readonly baseDamageMultiplier: number;
}

const DEFAULT_CONFIG: FlurryConfig = {
  cooldownMs: 800,                  // medium cooldown tier
  subHitCount: 3,                   // three rapid sub-strikes
  subHitIntervalMs: 80,             // 80ms between each sub-hit
  baseArcDegrees: 120,              // same total arc as Slash
  baseDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FlurryPhase = 'idle' | 'active' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FlurrySkillEffect implements SkillEffect {
  readonly skillId = 'slash_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: FlurryDeps;
  private readonly config: FlurryConfig;

  private _phase: FlurryPhase = 'idle';
  private _phaseTimer = 0;
  private _subHitsRemaining = 0;
  private _subHitTimer = 0;
  private _subHitsLanded = 0;
  private _totalEnemiesHit = 0;
  private _activationCount = 0;
  private _resolvedArcDegrees = 120;
  private _resolvedDamageMultiplier = 1.0;
  private _subArcDegrees = 40;

  constructor(deps: FlurryDeps, config: Partial<FlurryConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): FlurryPhase { return this._phase; }
  get subHitsLanded(): number { return this._subHitsLanded; }
  get totalEnemiesHit(): number { return this._totalEnemiesHit; }
  get activationCount(): number { return this._activationCount; }
  get resolvedArcDegrees(): number { return this._resolvedArcDegrees; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }
  get subArcDegrees(): number { return this._subArcDegrees; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle';
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve arc width (base + Extended Arc via 'slash' inheritance)
    const arcBonus = this.deps.resolver.getAdditiveEffect('slash', 'arcDegrees');
    this._resolvedArcDegrees = this.config.baseArcDegrees + arcBonus;

    // Each sub-hit covers 1/3 of the total arc
    this._subArcDegrees = this._resolvedArcDegrees / this.config.subHitCount;

    // Resolve damage multiplier
    this._resolvedDamageMultiplier = this.config.baseDamageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('slash', 'damage');

    // Enter active phase
    this._phase = 'active';
    this._subHitsRemaining = this.config.subHitCount;
    this._subHitTimer = 0;
    this._subHitsLanded = 0;
    this._activationCount++;

    // Fire first sub-hit immediately
    this.fireSubHit();

    return true;
  }

  deactivate(): void {
    // Cannot manually deactivate during sequence
  }

  isActive(): boolean {
    return this._phase === 'active';
  }

  update(delta: number): void {
    switch (this._phase) {
      case 'active':
        if (this._subHitsRemaining > 0) {
          this._subHitTimer += delta;
          if (this._subHitTimer >= this.config.subHitIntervalMs) {
            this._subHitTimer -= this.config.subHitIntervalMs;
            this.fireSubHit();
          }
        } else {
          // All sub-hits fired — enter cooldown
          this._phase = 'cooldown';
          this._phaseTimer = this.config.cooldownMs;
        }
        break;

      case 'cooldown':
        this._phaseTimer = Math.max(0, this._phaseTimer - delta);
        if (this._phaseTimer <= 0) {
          this._phase = 'idle';
        }
        break;
    }
  }

  destroy(): void {
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._subHitsRemaining = 0;
  }

  // --- Internal ---

  private fireSubHit(): void {
    if (this._subHitsRemaining <= 0) return;

    const enemiesHit = this.deps.combat.startArcAttack('flurry', this._subArcDegrees);
    this._totalEnemiesHit += enemiesHit;
    this._subHitsLanded++;
    this._subHitsRemaining--;
  }
}
